import {
  FormLabel,
  SelectChangeEvent,
  SimpleSelect,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@chronosphereio/chrono-ui';
import { FeatureFlagGate } from '@chronosphereio/core';
import { produce } from 'immer';
import { startCase } from 'lodash';
import { InformationOutline as InfoIcon } from 'mdi-material-ui';
import { useCallback } from 'react';
import { CollapseOptions } from '@/components';
import {
  isValidTruncationStrategy,
  PrometheusQueryReducer,
  PrometheusQuerySpec,
  PrometheusQueryType,
  SERIES_NAME_TYPE_DEFAULT,
  SERIES_NAME_TYPE_OPTIONS,
  SeriesNameType,
} from '@/core';
import { useVizFrameworkConfig } from '@/plugin-system';
import { adjustStepForScrapeAndIncrease, queryHasIncreaseWithInterval } from '../../model/time';
import { PrometheusTimeSeriesQueryEditorProps, REDUCER_OPTIONS, useEditorState } from './query-editor-model';

const MIN_STEP_INCREASE_ADJUSTED_HELP_TEXT =
  'The minimum step for the query. Adjusted to be 2x the scrape interval when using the increase function with $__interval variable (to ensure there are enough data points for the calculation).';

type PrometheusOptionsEditor = Pick<PrometheusTimeSeriesQueryEditorProps, 'onChange'> & {
  spec: PrometheusQuerySpec;
};

export function PrometheusOptionsEditor(props: PrometheusOptionsEditor) {
  const { onChange } = props;
  const { spec, dataType } = useEditorState();
  const config = useVizFrameworkConfig();

  const handleQueryTypeChange = useCallback(
    (_: React.MouseEvent, value: PrometheusQueryType) => {
      const newSpec = produce(spec, (draft) => {
        draft.queryType = value;
        if (value === 'Instant') {
          delete draft.reducer;
        } else {
          draft.reducer = 'Avg';
        }
      });
      onChange(newSpec);
    },
    [onChange, spec]
  );

  const handleReducerChange = useCallback(
    (e: SelectChangeEvent) => {
      onChange({ ...spec, reducer: e.target.value as PrometheusQueryReducer });
    },
    [onChange, spec]
  );

  const showSeriesNameEditor =
    dataType === 'SingleValue' || dataType === 'SingleValueTimeSeries' || dataType === 'TimeSeries' || !dataType;

  const showInstantOrRangeToggle =
    dataType === 'SingleValue' || dataType === 'SingleValueTimeSeries' || dataType === 'Table';

  const showMinStepEditor =
    !showInstantOrRangeToggle || spec.queryType === 'Range' || dataType === 'SingleValueTimeSeries';

  const seriesNameSummary = spec.series_name?.name_pattern ?? spec.series_name_format ?? 'Auto';

  const titleSummaryParts: string[] = [];
  if (showSeriesNameEditor) titleSummaryParts.push(`Series name: ${seriesNameSummary}`);
  if (showInstantOrRangeToggle) {
    titleSummaryParts.push(`Query type: ${spec.queryType || 'Instant'}`);
    if (spec.queryType === 'Range') titleSummaryParts.push(`Reduce values to: ${spec.reducer}`);
  }
  if (showMinStepEditor) titleSummaryParts.push(`Min step: ${spec.min_step || 'Auto'}`);
  if (config?.enableExperimentalPrometheusDownsampling === true) {
    titleSummaryParts.push(`Downsampling strategy: ${startCase(spec.downsampling_strategy ?? 'off')}`);
  }
  titleSummaryParts.push(`Truncation strategy: ${startCase(spec.truncation_strategy ?? 'auto')}`);

  return (
    <CollapseOptions
      variant="secondary"
      title="Options"
      titleSummary={titleSummaryParts.map((part, i) => (
        // Use an array of spans so that `text-overflow: ellipsis` works.
        <span key={i} style={{ marginRight: 16 }}>
          {part}
        </span>
      ))}
    >
      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
        {showSeriesNameEditor && <SeriesNameEditor />}

        {showInstantOrRangeToggle && (
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <FormLabel htmlFor={'query-type-control'} sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant="body2">Query type</Typography>
              </FormLabel>
              <ToggleButtonGroup value={spec.queryType ?? 'Instant'} onChange={handleQueryTypeChange}>
                <ToggleButton aria-label="Instant" value="Instant">
                  Instant
                </ToggleButton>
                <ToggleButton aria-label="Range" value="Range">
                  Range
                </ToggleButton>
              </ToggleButtonGroup>
              <Tooltip Content="Query for latest value or query for values over the time range">
                <InfoIcon fontSize="inherit" color="secondary" />
              </Tooltip>
            </Stack>
            {spec.queryType === 'Range' && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                  Reduce values in time range to
                </Typography>
                <SimpleSelect options={REDUCER_OPTIONS} value={spec.reducer ?? 'Avg'} onChange={handleReducerChange} />
              </Stack>
            )}
          </Stack>
        )}

        {showMinStepEditor && <MinStepEditor />}

        <FeatureFlagGate condition={() => config?.enableExperimentalPrometheusDownsampling === true}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SimpleSelect
              label="Downsampling strategy"
              options={['Off', 'LTTB', 'Avg', 'Sum', 'Min', 'Max'].map((v) => ({ label: v, key: v }))}
              value={spec.downsampling_strategy === undefined ? 'Off' : spec.downsampling_strategy}
              onChange={(v) => {
                let val: undefined | string = v.target.value;
                if (val === 'Off') {
                  val = undefined;
                }
                onChange({ ...spec, downsampling_strategy: val as 'Avg' | 'Sum' | 'Min' | 'Max' | 'LTTB' | undefined });
              }}
              sx={{ width: 180 }}
            />
            <Tooltip Content="Downsampling strategy to apply to the query. Note: this modifies the raw query.">
              <InfoIcon fontSize="inherit" color="secondary" />
            </Tooltip>
          </Stack>
        </FeatureFlagGate>

        <Stack direction="row" spacing={1} alignItems="center">
          <SimpleSelect
            label="Truncation strategy"
            options={['Auto', 'Off', 'Avg', 'Min', 'Max'].map((v) => ({ label: v, key: v }))}
            value={spec.truncation_strategy === undefined ? 'Auto' : spec.truncation_strategy}
            onChange={(v) => {
              const value = v.target.value;
              if (value === 'Auto') {
                onChange({ ...spec, truncation_strategy: undefined });
              } else if (isValidTruncationStrategy(value)) {
                onChange({ ...spec, truncation_strategy: value });
              }
            }}
            sx={{ width: 180 }}
          />
          <Tooltip Content="Truncation strategy to apply to the query. Note: this modifies the raw query.">
            <InfoIcon fontSize="inherit" color="secondary" />
          </Tooltip>
        </Stack>
      </Stack>
    </CollapseOptions>
  );
}

function MinStepEditor() {
  const { spec, setSpec, triggerOnChange } = useEditorState();

  // Account for scrape interval step adjustments so user is aware how automatic min step calculation works.
  const { prometheusScrapeIntervalSeconds: scrapeIntervalSeconds } = useVizFrameworkConfig() ?? {};

  const isIncreaseWithInterval = queryHasIncreaseWithInterval(spec.query);
  const placeholderText = scrapeIntervalSeconds
    ? `${adjustStepForScrapeAndIncrease({
        stepSeconds: scrapeIntervalSeconds,
        scrapeIntervalSeconds,
        isIncreaseWithInterval,
      })}s`
    : 'Ex. 10s, 5m';
  const tooltipHelpText = isIncreaseWithInterval
    ? MIN_STEP_INCREASE_ADJUSTED_HELP_TEXT
    : `The minimum step for the query. Cannot be less than the configured scrape interval of ${scrapeIntervalSeconds}s.`;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        label="Min step"
        placeholder={placeholderText}
        value={spec.min_step ?? ''}
        onChange={(e) =>
          setSpec((draft) => {
            draft.min_step = e.target.value;
          })
        }
        onBlur={triggerOnChange}
        sx={{ width: '180px' }}
      />
      <Tooltip Content={tooltipHelpText}>
        <InfoIcon fontSize="inherit" color="secondary" />
      </Tooltip>
    </Stack>
  );
}

export function SeriesNameEditor() {
  const { spec, triggerOnChange, setSpec } = useEditorState();

  const handleSeriesNameTypeChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      setSpec((draft) => {
        draft.series_name_format = undefined;
        draft.series_name = { type: e.target.value as SeriesNameType };
      });
    },
    [setSpec]
  );

  const handleLabelNamePatternChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSpec((draft) => {
        draft.series_name_format = undefined;
        draft.series_name = {
          type: SeriesNameType.Labels,
          name_pattern: e.target.value || undefined,
        };
      });
    },
    [setSpec]
  );

  const handleRegexChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSpec((draft) => {
        draft.series_name_format = undefined;
        draft.series_name = {
          ...draft.series_name,
          type: SeriesNameType.Regex,
          regex: e.target.value || undefined,
        };
      });
    },
    [setSpec]
  );

  const handleRegexNamePatternChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSpec((draft) => {
        draft.series_name_format = undefined;
        draft.series_name = {
          ...draft.series_name,
          type: SeriesNameType.Regex,
          name_pattern: e.target.value || undefined,
        };
      });
    },
    [setSpec]
  );

  const showNameByRegex = spec.series_name?.type === SeriesNameType.Regex;
  const showNameByLabels = !showNameByRegex;

  return (
    <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
      <SimpleSelect
        label="Name series with"
        options={SERIES_NAME_TYPE_OPTIONS}
        value={spec.series_name?.type ?? SERIES_NAME_TYPE_DEFAULT}
        onChange={handleSeriesNameTypeChange}
        sx={{ width: '180px' }}
      />
      {showNameByLabels && (
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            label="Pattern"
            placeholder="Ex. {{ env }}"
            value={spec.series_name?.name_pattern ?? spec.series_name_format ?? ''}
            onChange={handleLabelNamePatternChange}
            onBlur={triggerOnChange}
          />
          <Tooltip Content="Name series by using the syntax: {{ label_name }}. Ex. {{ env }}-{{ instance }}.">
            <InfoIcon fontSize="inherit" color="secondary" />
          </Tooltip>
        </Stack>
      )}
      {showNameByRegex && spec.series_name?.type === SeriesNameType.Regex && (
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            label="Regex"
            placeholder="Ex. production-(.*)"
            value={spec.series_name?.regex ?? ''}
            onChange={handleRegexChange}
            onBlur={triggerOnChange}
          />
          <TextField
            fullWidth
            label="Pattern"
            placeholder="Ex. Prod: $1"
            value={spec.series_name?.name_pattern ?? ''}
            onChange={handleRegexNamePatternChange}
            onBlur={triggerOnChange}
          />
          <Tooltip Content="Name series by defining a regex and using $1, $2, etc. to include capture groups">
            <InfoIcon fontSize="inherit" color="secondary" />
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );
}
